import React, { useState, useEffect } from "react";
import RectangleClassifieds from "../../../creativePreviews/RectangleClassifieds";
import PromotedImagePreview from "../../../creativePreviews/PromotedImagePreview";
import PromotedVideoPreview from "../../../creativePreviews/PromotedVideoPreview";
import HalfVideoPreview from "../../../creativePreviews/HalfVideoPreview";
import { Toast } from "../../../shared/Toast";
import { useTableState } from "../utils/useTableState";
import ImageModal from "./ImageModal";
import config from "../../../config";
import { proxyDownload } from "../../../utils/proxyDownload";

const formatDate = (timestamp) => {
  return new Date(timestamp * 1000).toLocaleDateString();
};

const getRejection = (input = "") => {
  const description = input.split("Rejection Reason:")[0].trim() || "";
  const rejection = (input.match(/Rejection Reason: (.*?)(?=\n|$)/g) || []).pop()?.replace("Rejection Reason: ", "").trim() || "";
  return { description, rejection };
};

const AdDetailsContent = ({ adData }) => {
  const imgUrls = adData.img_urls || {};
  const videoUrl =  adData.video_urls ?? null;
  console.log(adData);
  const otherImages = Object.entries(imgUrls)
    .filter(([key]) => key !== "sub")
    .map(([_, value]) => value)
    .filter(Boolean)
    .map((url) => ({ url, type: "main" }));

  const subImages = Array.isArray(imgUrls.sub)
    ? imgUrls.sub.map((obj) => ({
        url: obj.url || obj,
        text: obj.text || null,
        type: "sub",
      }))
    : [];

  const images = [...otherImages, ...subImages];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const isPromotedVideoStory = adData.ad_creative_sub_type === "promoted_video_story";

  useEffect(() => {
    if (isPromotedVideoStory) {
      setShowVideo(!!videoUrl);
    }
  }, [isPromotedVideoStory, videoUrl]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const files = [];
      if (imgUrls.main) files.push(imgUrls.main);
      if (Array.isArray(imgUrls.sub)) {
        imgUrls.sub.forEach((obj) => {
          if (typeof obj === "string") files.push(obj);
          else if (obj.url) files.push(obj.url);
        });
      }
      if (videoUrl) files.push(videoUrl);
      console.log(files);
      await proxyDownload(files, adData.creative_name || "download");
    } catch (error) {
      Toast("failed", "Download failed.");
    } finally {
      setIsDownloading(false);
    }
  };

  const { modal, setModal, actionHandlers } = useTableState();

  const previewProps = {
    img_urls: adData.latest_design || imgUrls.thumbnail || "",
    video_urls: videoUrl,
    head_line: adData.head_line,
    description: adData.description,
    click_url: adData.click_url,
    button_enabled: 0,
    button_link: adData.click_url,
    name: adData.creative_name,
    position: adData.position,
  };

  return (
    <div className="flex flex-col md:flex-row p-4 gap-4 overflow-x-hidden">
      <div className="w-full md:w-2/5 flex flex-col gap-3">
        <div className="border rounded-lg p-3 bg-gray-100">
          <h3 className="text-lg font-semibold mb-2">Campaign Details</h3>
          <p><span className="font-medium">Campaign Name:</span> {adData.creative_name}</p>
          <p><span className="font-medium">Brand:</span> {adData.brand_name}</p>
          <p><span className="font-medium">Client:</span> {adData.client_name}</p>
          <p><span className="font-medium">Ad Type:</span> {adData.ad_type}</p>
        </div>

        <div className="border rounded-lg p-3 bg-gray-100">
          <h3 className="text-lg font-semibold mb-2">Dates</h3>
          <p><span className="font-medium">Start Date:</span> {formatDate(adData.creative_start_date)}</p>
          <p><span className="font-medium">End Date:</span> {formatDate(adData.creative_start_date,'end')}</p>
          <p><span className="font-medium">Ad Creation Date:</span> {formatDate(adData.created_at)}</p>
          <p><span className="font-medium">Completed Date:</span> {adData.status === 9 ? formatDate(adData.completed_date) : "--"}</p>
        </div>

        <div className="border rounded-lg p-3 bg-gray-100">
          <h3 className="text-lg font-semibold mb-2">Sales Person</h3>
          <p><span className="font-medium">Name:</span> {adData.sales_person.sales_person_name}</p>
          <p><span className="font-medium">Mobile:</span> {adData.sales_person.mobile}</p>
          <p><span className="font-medium">Assigned To:</span> {adData.designer_name}</p>
        </div>

        <div className="border rounded-lg p-3 bg-gray-100">
          <p className="font-bold">Rough Description:</p>
          <p>{getRejection(adData?.rough_description)?.description || "No description available"}</p>
          {getRejection(adData?.rough_description)?.rejection && (
            <div className="border rounded-lg p-3 mt-4 bg-red-100">
              <p className="font-bold text-red-600">Rejection Reason:</p>
              <p>{getRejection(adData?.rough_description)?.rejection}</p>
            </div>
          )}
        </div>
      </div>

      <div className="w-full md:w-3/5 border rounded p-4 flex flex-col gap-4 bg-gray-100">
        <div>
          <h3 className="text-lg font-semibold mb-2">Creative Preview</h3>
          <p><span className="font-medium">Creative Type:</span> {adData.creative_type}</p>
        </div>

        {adData.ad_type === "classified" ? (
          <RectangleClassifieds preview={{
            cla_brand_caption: adData.cla_brand_caption,
            logo_urls: adData.cla_brand_img,
            brand_name: adData.cla_brand_name,
            btn_text_bgclr: adData.cla_btn_bg_clr,
            btn_icon: adData.cla_btn_icon,
            button_text: adData.cla_btn_txt,
            btn_text_clr: adData.cla_btn_txt_col,
            btn_url: adData.cla_btn_url,
            no_of_rec: adData.cla_no_of_rec,
            sub_type: adData.cla_sub_type,
            img_urls: adData.img_urls?.classified,
            head_line: adData.head_line || adData.headline,
            description: adData.description,
            button_enabled: !!adData.cla_btn_txt,
          }} />
        ) : isPromotedVideoStory ? (
          <div className="w-full">
            {showVideo ? (
              videoUrl ? (
                <PromotedVideoPreview preview={previewProps} />
              ) : (
                <div className="text-center text-red-500 py-8 bg-gray-50 rounded">
                  Video file not available
                </div>
              )
            ) : (
              imgUrls.main ? (
                <PromotedImagePreview
                  preview={{
                    ...previewProps,
                    video_urls: null,
                  }}
                />
              ) : (
                <div className="text-center text-gray-500 py-8 bg-gray-50 rounded">
                  No image available
                </div>
              )
            )}
          </div>
        ) : adData.ad_creative_sub_type === "promoted_image_story" ? (
          <div className="w-full">
            <h4 className="text-sm font-semibold mb-2 text-gray-700">Promoted Image Story Preview:</h4>
            <PromotedImagePreview preview={previewProps} />
          </div>
        ) : adData.ad_creative_sub_type === "half_video" ? (
          <div className="w-full">
            <h4 className="text-sm font-semibold mb-2 text-gray-700">Half Video Preview:</h4>
            <HalfVideoPreview preview={previewProps} />
          </div>
        ) : images.length > 0 ? (
          <div className="flex flex-col items-center justify-center w-full max-w-3xl mx-auto gap-4">
            <div className="w-full bg-gray-50 border rounded-lg p-3 mb-2">
              <h4 className="text-sm font-semibold mb-2 text-gray-700">Image Types:</h4>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center text-white text-xs font-bold">M</div>
                  <span className="text-gray-600">Main Images</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded flex items-center justify-center text-white text-xs font-bold">S</div>
                  <span className="text-gray-600">Sub Images</span>
                </div>
              </div>
            </div>

            <div className="w-full sticky top-0 z-20 bg-white border-b py-2 px-4">
              <div className="overflow-x-auto">
                <div className="flex items-center gap-3 mb-2">
                  {images.map((imageObj, idx) => (
                    <div key={idx} className="relative flex-shrink-0">
                      <img
                        src={imageObj.url}
                        alt={`thumb-${idx}`}
                        onClick={() => setCurrentIndex(idx)}
                        className={`w-16 h-16 object-cover rounded-lg cursor-pointer border-2 transition-transform duration-200 ${idx === currentIndex ? "border-blue-500 ring-2 ring-blue-400 scale-105 m-1" : "border-gray-300 hover:border-blue-300"}`}
                      />
                      <div className={`absolute top-0 right-0 px-1 py-0.5 text-xs font-bold rounded-bl-md rounded-tr-lg shadow-sm ${imageObj.type === "main" ? "bg-green-500 text-white" : "bg-orange-500 text-white"}`}>
                        {imageObj.type === "main" ? "M" : "S"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {images[currentIndex]?.type === "sub" && images[currentIndex]?.text && (
              <p className="text-center mt-2 text-black px-2 py-1 bg-zinc-300 rounded-lg">
                {"TEXT : " + images[currentIndex].text}
              </p>
            )}

            <div className="relative w-full bg-white rounded-lg shadow-md overflow-hidden">
              <img
                src={images[currentIndex].url}
                alt={`creative-${currentIndex}`}
                className="w-full h-auto object-contain transition-opacity duration-300 ease-in-out"
                onClick={() => {
                  setModal({
                    open: true,
                    type: "latestDesign",
                    image: images[currentIndex].url,
                  });
                }}
              />
              <div className={`absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center font-bold text-white shadow-lg ${images[currentIndex].type === "main" ? "bg-green-600" : "bg-orange-600"}`}>
                {images[currentIndex].type === "main" ? "M" : "S"}
              </div>
              {images.length > 1 && (
                <>
                  <button onClick={handlePrev} className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 focus:outline-none z-10" aria-label="Previous Image">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button onClick={handleNext} className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 focus:outline-none z-10" aria-label="Next Image">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            <p className="text-sm text-gray-500">
              Image <span className="font-medium">{currentIndex + 1}</span> of {images.length}
            </p>
          </div>
        ) : (
          <div className="font-bold text-lg text-center flex items-center justify-center w-full h-64 border rounded bg-gray-100 text-gray-500">
            NO MEDIA FOUND
          </div>
        )}

        <button
          className={`mx-3 px-2 py-1 rounded-lg shadow-md ${
            isDownloading ? "bg-green-500 cursor-progress" : "bg-blue-400"
          }`}
          onClick={handleDownload}
          disabled={isDownloading}
        >
          Download{" "}
          {adData.ad_type === "classified"
            ? "Preview"
            : videoUrl || images.length > 1
            ? "All as ZIP"
            : "Preview"}
        </button>
      </div>

      <ImageModal
        isOpen={modal?.open && modal?.type === "latestDesign"}
        image={modal?.image}
        alt="alt image"
        onClose={actionHandlers.closeModal}
        row={modal?.row}
      />
    </div>
  );
};

export default AdDetailsContent;